/**
 * Star Shop modal (STA-144). Stardust packs + monthly subscriptions.
 * Triggered from the nav's gold Stardust pill.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError, type StardustPack } from '../api';
import { track } from '../analytics';
import { launchCheckout } from './CheckoutLauncher';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import { paymentsEnabled } from '../config';

type Tab = 'stardust' | 'subscribe';

export default function StarShopModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>('stardust');
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [packs, setPacks] = useState<StardustPack[]>([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [refundAgreed, setRefundAgreed] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Lock body scroll while modal is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  useEffect(() => {
    void api.stardustPacks()
      .then(setPacks)
      .catch(() => setPacks([]))
      .finally(() => setPacksLoading(false));
  }, []);

  const buyPack = async (pack: StardustPack) => {
    if (!user) {
      onClose();
      nav('/login');
      return;
    }
    if (!refundAgreed) {
      setError('請先勾選同意退費政策');
      return;
    }
    setError(null);
    setLoading(pack.id);
    track('stardust_pack_click', { pack_id: pack.id });
    try {
      const result = await api.buyStardustPack(pack.id, {
        return_url: `${window.location.origin}/account?topup=ok`,
        refund_policy_agreed: true,
      });
      launchCheckout(result);
    } catch (e) {
      setLoading(null);
      setError(formatApiError(e, '付款啟動失敗，請稍後再試。'));
    }
  };

  const subscribe = async (provider: 'newebpay' | 'stripe') => {
    if (!user) {
      onClose();
      nav('/login');
      return;
    }
    if (!refundAgreed) {
      setError('請先勾選同意退費政策');
      return;
    }
    setError(null);
    setLoading(provider);
    track('checkout_started', { provider, source: 'star_shop_modal' });
    try {
      const result = await api.checkout(
        provider,
        provider === 'stripe' ? `${window.location.origin}/account?checkout=ok` : undefined,
        true,
      );
      launchCheckout(result);
    } catch (e) {
      setLoading(null);
      setError(formatApiError(e, '付款啟動失敗，請稍後再試。'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('shop.title')}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-tarot-gold/40 bg-tarot-card p-6 sm:p-8"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-tarot-gold/15 text-tarot-muted hover:text-tarot-gold text-xl leading-none"
          aria-label={t('shop.close')}
        >
          ×
        </button>

        <div className="text-center mb-6">
          <div className="mp-eyebrow">{t('shop.title').toUpperCase()}</div>
          <h2 className="mp-display text-3xl text-tarot-gold mt-2">{t('shop.title')}</h2>
          <p className="text-tarot-muted text-sm mt-3">
            {t('shop.balance')}：<span className="text-tarot-gold">★ {user?.stardust ?? 0}</span>
          </p>
        </div>

        {paymentsEnabled && (
          <div className="flex justify-center gap-2 mb-6 text-sm">
            <button
              type="button"
              onClick={() => setTab('stardust')}
              className={`px-4 py-2 rounded-full border ${
                tab === 'stardust'
                  ? 'border-tarot-gold bg-tarot-gold/15 text-tarot-gold'
                  : 'border-tarot-gold/25 text-tarot-muted hover:text-tarot-text'
              }`}
            >
              {t('shop.tab_stardust')}
            </button>
            <button
              type="button"
              onClick={() => setTab('subscribe')}
              className={`px-4 py-2 rounded-full border ${
                tab === 'subscribe'
                  ? 'border-tarot-gold bg-tarot-gold/15 text-tarot-gold'
                  : 'border-tarot-gold/25 text-tarot-muted hover:text-tarot-text'
              }`}
            >
              {t('shop.tab_subscribe')}
            </button>
          </div>
        )}

        {!paymentsEnabled ? (
          <ComingSoonPanel />
        ) : (
          <>
            {tab === 'stardust' && (
              <>
                {packsLoading ? (
                  <div className="text-tarot-muted text-center py-8 text-sm">載入中…</div>
                ) : packs.length === 0 ? (
                  <div className="text-tarot-muted text-center py-8 text-sm">
                    目前沒有可購買的方案。
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {packs.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        disabled={loading !== null}
                        onClick={() => buyPack(p)}
                        className="card !p-4 text-center hover:border-tarot-gold transition-colors relative disabled:opacity-50"
                      >
                        {p.badge && (
                          <span
                            className={`absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] tracking-widest px-2 py-0.5 rounded-full border ${
                              p.badge === 'popular'
                                ? 'border-tarot-mauve text-tarot-mauve bg-tarot-mauve/10'
                                : 'border-tarot-gold text-tarot-gold bg-tarot-gold/10'
                            }`}
                          >
                            {t(p.badge === 'popular' ? 'shop.popular' : 'shop.best_value')}
                          </span>
                        )}
                        <div className="mp-display text-2xl text-tarot-gold">★ {p.amount.toLocaleString()}</div>
                        {p.bonus > 0 && (
                          <div className="text-xs text-tarot-mauve mt-1">
                            {t('shop.bonus', { n: p.bonus })}
                          </div>
                        )}
                        <div className="text-tarot-muted text-sm mt-2">
                          NT$ {p.price_twd.toLocaleString()}
                        </div>
                        {loading === p.id && (
                          <div className="text-xs text-tarot-gold mt-2">啟動付款…</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {tab === 'subscribe' && (
              <div className="space-y-3">
                <div className="card border-tarot-gold/60 ring-1 ring-tarot-gold/30">
                  <div className="flex items-baseline justify-between">
                    <h3 className="mp-display text-2xl text-tarot-gold">Premium</h3>
                    <div>
                      <span className="text-3xl">NT$199</span>
                      <span className="text-tarot-muted ml-2">/ 月</span>
                    </div>
                  </div>
                  <ul className="mt-4 space-y-1 text-sm text-tarot-text">
                    <li>· 無限次抽牌</li>
                    <li>· 完整解讀 + 跨對話記憶</li>
                    <li>· 優先支援 · 免費追問</li>
                  </ul>
                  <p className="text-xs text-tarot-muted mt-3">{t('shop.cancel_anytime')}</p>
                  <div className="mt-5 grid gap-2">
                    <button
                      disabled={loading !== null}
                      onClick={() => subscribe('newebpay')}
                      className="btn-primary w-full"
                    >
                      {loading === 'newebpay' ? '啟動付款…' : '訂閱（信用卡 / LINE Pay / ATM）'}
                    </button>
                    <button
                      disabled={loading !== null}
                      onClick={() => subscribe('stripe')}
                      className="btn-ghost w-full text-sm"
                    >
                      {loading === 'stripe' ? '啟動付款…' : 'Pay with international card (USD)'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <label className="mt-5 flex items-start gap-2 text-xs text-tarot-muted cursor-pointer">
              <input
                type="checkbox"
                checked={refundAgreed}
                onChange={(e) => setRefundAgreed(e.target.checked)}
                className="mt-0.5 accent-tarot-gold"
              />
              <span>
                已閱讀並同意 <a href="/refund-policy" target="_blank" rel="noreferrer" className="text-tarot-gold underline">退費政策</a>
                （數位內容於開通後不適用 7 天鑑賞期）。
              </span>
            </label>

            {error && (
              <p className="mt-4 text-center text-sm text-red-400">{error}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ComingSoonPanel() {
  return (
    <div className="text-center py-10" data-testid="starshop-coming-soon">
      <div className="text-5xl mb-4" aria-hidden="true">✦</div>
      <h3 className="mp-display text-2xl text-tarot-gold">敬請期待</h3>
      <p className="text-tarot-muted text-sm mt-3 max-w-sm mx-auto leading-relaxed">
        付款服務即將上線（藍新金流審核中），上線後即可儲值 Stardust 與訂閱 Premium。
        在此期間，仍可透過每日打卡與邀請好友累積 Stardust。
      </p>
      <button
        type="button"
        disabled
        aria-disabled="true"
        className="btn-primary mt-6 opacity-60 cursor-not-allowed"
      >
        敬請期待
      </button>
    </div>
  );
}
